<script>
export default {
	name: 'DestinationEssentialDetails',
	inheritAttrs: false,
};
</script>

<script setup>
import AppHyperlink from '@/components/AppHyperlink.vue';
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { isMobileViewport, isLG } from '~/logic/composables/breakpoints';
import { isEmpty } from 'lodash';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { isRTL } from '~/logic/helpers/is-rtl.js';
const { isEditorMode } = useGlobalAEMState();

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const props = defineProps({
	destinationEssentialList: { type: Array, default: () => [] },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	detailsType: { type: Array, default: () => [] },
});

/*
*	@customImgPath String parameter
*	@imageType String parameter
*
*	Return background image inline-style string
*	Otherwise return empty string
*/
const iconImgFunc = (customImgPath, imageType) => {
	if ( customImgPath !== null && imageType === 'iconImage') return `background-image: url(${customImgPath})`;
	return '';
};

const timezoneName = computed(() => {
	if (props.detailsType.length !== 0) {
		const [ localTimeName ] = props.detailsType.map((item) => {
			return item.localTime;
		}).filter((filteredItem) => filteredItem !== null);

		return localTimeName;
	}
});

const currentExchangeRate = computed(() => {

	if (props.detailsType.length !== 0) {
		const [ currencyRate ] = props.detailsType.map((item) => {
			return item.currency;
		}).filter((filteredItem) => filteredItem !== null);
		
		if (!isEmpty(currencyRate)) {
			return currencyRate;
		}
		
	}
	
	return {
		exchangeRate: null,
		lastUpdated: null,
		currencyLabel: null,
	};
});
/*
*	Return formatted time
*	For example: 
*	LTR --> 23 Mar 2023, 08:03
*	RTL --> 08:03, 23 Mar 2023
*/
const lastUpdatedFormatted = (lastUpdateTime) => {
	if (isRTL()) {
		const currentInstance = dayjs(lastUpdateTime).tz(timezoneName.value).locale('en');
		return currentInstance.format('HH:mm, DD MMM YYYY');
	}
	
	return dayjs(lastUpdateTime).tz(timezoneName.value).format('DD MMM YYYY, HH:mm');
};


const currentTimeDate = ref('');

/*
*	Return current user timezone with formatted time
*	For example: 
*	LTR --> 19:03, 23 March 2023 (GMT+11)
*	RTL --> 19:03, 23 March 2023 (GMT+11)
*/
const currentTimeDateFormatSetter = () => {
	if (timezoneName.value) {
		if (isRTL()) {
			const currentInstance = dayjs().tz(timezoneName.value).locale('en');
			currentTimeDate.value = currentInstance.format(`HH:mm, DD MMMM YYYY (z)`);
		}

		currentTimeDate.value = dayjs().tz(timezoneName.value).format(`HH:mm, DD MMMM YYYY (z)`);
	}
};

const isBaseTargetCurrencyNull = () => {
	return currentExchangeRate.value.exchangeRate === null
		|| currentExchangeRate.value.lastUpdated === null
		|| currentExchangeRate.value.currencyLabel === null;
};

const isTargetTimezoneNull = () => {
	return timezoneName.value === ''
		|| timezoneName.value === null;
};

const isCurrencyOrlocalTimeSelected = computed(() => {
	return props.destinationEssentialList.some((item) => {
		return item.type === 'currency' || item.type === 'localTime';
	});
});

onMounted(() => {
	currentTimeDateFormatSetter();
	
	setInterval(() => {
		currentTimeDateFormatSetter();
	}, 1000);
});
</script>

<template>
<template v-if="isCurrencyOrlocalTimeSelected && (isBaseTargetCurrencyNull() && isTargetTimezoneNull())">
	<p v-if="isEditorMode" class="text-center py-11">Base Currency in Root page or Target Currency in Destination page is not configured properly. Detail Type: "Currency" and "Local Time" can only be configured within a destination details page template.</p>
</template>

<template v-else>
	<div
		v-if="(isMobileViewport && !props.isHideInMobile) || (!isMobileViewport && !props.isHideInDesktop)"
		class="DestinationEssentialDetails"
	>
		<div class="DestinationEssentialBlock">
			<ul class="DestinationEssentialDetailsList">
				<template
					v-for="(destinationEssentialItem, destinationEssentialIndex) in props.destinationEssentialList"
					:key="destinationEssentialIndex"
				>

					<!-- Type: Currency -->
					<li
						v-if="destinationEssentialItem.type === 'currency' && !isBaseTargetCurrencyNull()"
						:key="`currency-${destinationEssentialIndex}`"
						class="DestinationEssentialDetailsItem"
					>
						<div class="">
							<div
								class="DestinationEssentialDetailsItemIcon"
								:class="[destinationEssentialItem.batikBackground]"
								:style="[iconImgFunc( destinationEssentialItem.image, destinationEssentialItem.iconType )]"
							>
								<!-- <icon-fal-wallet class="text-7xl lg:text-4xl fill-white" /> -->
								<FontAwesomeLazy
									v-if="destinationEssentialItem.iconType === 'iconFontAwsomeId'"
									:iconId="destinationEssentialItem.iconFontAwsomeId?.trim()"
									iconStyle="regular"
									class="text-neutral-white-base"
								/>
								
								<img
									v-else
									:src="destinationEssentialItem.iconImage"
								/>
							</div>
						</div>
						<div class="DestinationEssentialDetailsItemBody flex flex-col">
							<div
								class="BodyDescription"
								:class="{
									'BodyDescriptionTextAlignLeft': destinationEssentialItem.mobileTextAlignment === 'left' && (isMobileViewport || isLG),
									'BodyDescriptionTextAlignRight': destinationEssentialItem.mobileTextAlignment === 'right' && (isMobileViewport || isLG),
									'DBodyDescriptionTextAlignCenter': destinationEssentialItem.mobileTextAlignment === 'centre' && (isMobileViewport || isLG),
								}"
							>
								<h5 class="title >lg:text-center">{{ destinationEssentialItem.title }}</h5>
								<p class="information">
									<span v-html-sanitize="destinationEssentialItem.description" class="author-html-container"></span>
									<span class="block >lg:text-center font-semibold">{{ `${currentExchangeRate.exchangeRate} ${currentExchangeRate.currencyLabel}` }}</span>
								</p>
								<p>&nbsp;</p>
								<p class="description mb-4 text-sm >lg:text-center">{{ $t('Last updated') }}: <span class="inline-block currency-last-updated">{{ lastUpdatedFormatted(currentExchangeRate.lastUpdated) }}</span></p>
							</div>
							<AppHyperlink
								v-if="destinationEssentialItem.ctaText && destinationEssentialItem.ctaUrl"
								:href="destinationEssentialItem.ctaUrl"
								:target="destinationEssentialItem.openInNewTab ? '_blank' : '_self'"
								class="cta-btn inline-block text-primary-blue-base hover:text-primary-blue-light font-semibold flex-1 >lg:self-center"
							>
								<span>{{ destinationEssentialItem.ctaText }}</span>
								<icon-far-arrow-up-right-from-square
									v-if="destinationEssentialItem.externalLinkIcon"
									class="text-base ml-4 rtl:mr-4 inline-block fill-current"
								/>
								<icon-far-arrow-right
									v-else
									class="cta-btn-arrow-icon text-base ml-3 rtl:mr-3 inline-block fill-current"
								/>
							</AppHyperlink>
						</div>
					</li>

					<!-- Type: Language and time -->
					<li
						v-else-if="destinationEssentialItem.type === 'localTime' && !isTargetTimezoneNull()"
						:key="`localTime-${destinationEssentialIndex}`"
						class="DestinationEssentialDetailsItem"
					>
						<div class="">
							<div
								class="DestinationEssentialDetailsItemIcon"
								:class="[destinationEssentialItem.batikBackground]"
								:style="[iconImgFunc( destinationEssentialItem.image, destinationEssentialItem.iconType )]"
							>
								<!-- <icon-fal-comment-dots class="text-7xl lg:text-4xl fill-white" /> -->
								<FontAwesomeLazy
									v-if="destinationEssentialItem.iconType === 'iconFontAwsomeId'"
									:iconId="destinationEssentialItem.iconFontAwsomeId?.trim()"
									iconStyle="regular"
									class="text-neutral-white-base"
								/>
								
								<img
									v-else
									:src="destinationEssentialItem.iconImage"
								/>
							</div>

						</div>
						<div class="DestinationEssentialDetailsItemBody flex flex-col">
							<div
								class="BodyDescription"
								:class="{
									'BodyDescriptionTextAlignLeft': destinationEssentialItem.mobileTextAlignment === 'left' && (isMobileViewport || isLG),
									'BodyDescriptionTextAlignRight': destinationEssentialItem.mobileTextAlignment === 'right' && (isMobileViewport || isLG),
									'DBodyDescriptionTextAlignCenter': destinationEssentialItem.mobileTextAlignment === 'centre' && (isMobileViewport || isLG),
								}"
							>
								<h5 class="title >lg:text-center">{{ destinationEssentialItem.title }}</h5>
								<!-- <p class="information">National language <span class="inline-block font-semibold">Indonesian</span></p> -->
								<p
									v-html-sanitize="destinationEssentialItem.description"
									class="description mb-0 author-html-container"
								></p>
								<p class="font-bold mb-4 >lg:text-center localtime-updated">{{ currentTimeDate }}</p>
							</div>
							<AppHyperlink
								v-if="destinationEssentialItem.ctaText && destinationEssentialItem.ctaUrl"
								:href="destinationEssentialItem.ctaUrl"
								:target="destinationEssentialItem.openInNewTab ? '_blank' : '_self'"
								class="cta-btn inline-block text-primary-blue-base hover:text-primary-blue-light font-semibold >lg:self-center"
							>
								<span>{{ destinationEssentialItem.ctaText }}</span>
								<icon-far-arrow-up-right-from-square
									v-if="destinationEssentialItem.externalLinkIcon"
									class="text-base ml-4 rtl:mr-4 inline-block fill-current"
								/>
								<icon-far-arrow-right
									v-else
									class="cta-btn-arrow-icon text-base ml-3 rtl:mr-3 inline-block fill-current"
								/>
							</AppHyperlink>
						</div>
					</li>

					<!-- Type: None -->
					<li
						v-else-if="destinationEssentialItem.type === 'none'"
						:key="`none-${destinationEssentialIndex}`"
						class="DestinationEssentialDetailsItem"
					>
						<div class="">
							<div
								class="DestinationEssentialDetailsItemIcon"
								:class="[destinationEssentialItem.batikBackground]"
								:style="[iconImgFunc( destinationEssentialItem.image, destinationEssentialItem.iconType )]"
							>
								<!-- <icon-fal-plug class="text-7xl lg:text-4xl fill-white" /> -->
								<FontAwesomeLazy
									v-if="destinationEssentialItem.iconType === 'iconFontAwsomeId'"
									:iconId="destinationEssentialItem.iconFontAwsomeId?.trim()"
									iconStyle="regular"
									class="text-neutral-white-base"
								/>
								
								<img
									v-else
									:src="destinationEssentialItem.iconImage"
								/>
							</div>
						</div>
						<div class="DestinationEssentialDetailsItemBody flex flex-col">
							<div
								class="BodyDescription"
								:class="{
									'BodyDescriptionTextAlignLeft': destinationEssentialItem.mobileTextAlignment === 'left' && (isMobileViewport || isLG),
									'BodyDescriptionTextAlignRight': destinationEssentialItem.mobileTextAlignment === 'right' && (isMobileViewport || isLG),
									'DBodyDescriptionTextAlignCenter': destinationEssentialItem.mobileTextAlignment === 'centre' && (isMobileViewport || isLG),
								}"
							>
								<h5 class="title >lg:text-center">{{ destinationEssentialItem.title }}</h5>
								<!-- <p class="information">Voltage and frequency <span class="inline-block font-semibold">230V, 50Hz</span></p>
								<p class="description">Adapter is not required to use Malaysian plugs.</p> -->
								<p v-html-sanitize="destinationEssentialItem.description" class="description mb-4 author-html-container"></p>
							</div>
							<AppHyperlink
								v-if="destinationEssentialItem.ctaText && destinationEssentialItem.ctaUrl"
								:href="destinationEssentialItem.ctaUrl"
								:target="destinationEssentialItem.openInNewTab ? '_blank' : '_self'"
								class="cta-btn inline-block text-primary-blue-base hover:text-primary-blue-light font-semibold >lg:self-center"
							>
								<span>{{ destinationEssentialItem.ctaText }}</span>
								<icon-far-arrow-up-right-from-square
									v-if="destinationEssentialItem.externalLinkIcon"
									class="text-base ml-4 rtl:mr-4 inline-block fill-current"
								/>
								<icon-far-arrow-right
									v-else
									class="cta-btn-arrow-icon text-base ml-3 rtl:mr-3 inline-block fill-current"
								/>
							</AppHyperlink>
						</div>
					</li>
				</template>
			</ul>
		</div>
	</div>

	<div v-if="isEditorMode && props.isHideInDesktop">
		<div class="generic-container py-10 text-center">
			<p>Hidden in: <span v-if="props.isHideInDesktop">Desktop</span> <span v-if="props.isHideInMobile">, Mobile</span></p>
		</div>
	</div>
</template>
</template>

<style lang="scss">
@use '~/styles/partials/_var.scss';

/* FontAwesomeLazyIcon styling */
.DestinationEssentialDetailsItemIcon {
	svg {
		fill: currentColor;
		width: auto;
		height: 32px;

		@media #{var.$query-min-lg} {
			height: 80px;
		}
	}
}

</style>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.DestinationEssentialDetails {
	@apply my-16;
	
	@media #{var.$query-min-lg} {
		padding-left: 123px;
		padding-right: 123px;
		@apply my-20;
	}

	.DestinationEssentialDetailsHeading {
		font-size: 28px;
		
		@media #{var.$query-min-lg} {
			font-size: 40px;
		}
	}

	.DestinationEssentialBlock {
		margin-top: 32px;
		@media #{var.$query-min-lg} {
			margin-top: 54px;
		}
	}

	.DestinationEssentialDetailsList {
		@apply flex flex-col;
		@media #{var.$query-min-lg} {
			@apply flex-row justify-center;
		}
	}

	.DestinationEssentialDetailsItem {
		@apply flex;
		margin-bottom: 48px;
		margin-right: 16px;
		margin-left: 16px;

		@media #{var.$query-min-lg} {
			display: flex;
			flex-direction: column;
			width: calc(100% / 4);
			margin-bottom: 0;
		}

		&:not(:last-child) {
			@media #{var.$query-min-lg} {
				@apply mr-6;
			}
		}

		.DestinationEssentialDetailsItemIcon {
			// flex: 1 1 30%;
			width: 90px;
			height: 117px;
			border-radius: 200px;
			background-position: center;
			background-size: cover;
			background-repeat: no-repeat;
			@apply flex justify-center items-center mx-auto;

			@media #{var.$query-min-lg} {
				width: 202px;
				height: 262px;
			}

			&.teal {
				background-image: url('~/assets/usp/usp-background-pattern-teal.png');
			}

			&.gold {
				background-image: url('~/assets/usp/usp-background-pattern-gold.png');
			}

			&.blue {
				background-image: url('~/assets/usp/usp-background-pattern-blue.png');
			}
		}

		.DestinationEssentialDetailsItemBody {
			flex: 1 1 70%;
			@apply mt-6;
			
			@media #{var.$query-max-lg} {
				@apply ml-6 mt-0;
				
				html[dir="rtl"] & {
					@apply mr-6 ml-0;
				}
			}

			.title {
				@apply lg:text-lg lg:mt-3 mb-4 font-bold;
			}
		}
	}
}

.BodyDescription {
	flex-grow: 1;
	
	.description {
		word-break: break-word;
	}
}

.cta-btn {
	.cta-btn-arrow-icon {
		transition: transform .25s ease-in-out;
	}

	&:hover {
		.cta-btn-arrow-icon {
			@apply translate-x-0.5;
		}
	}
	
	html[dir="rtl"] & {
		.cta-btn-arrow-icon {
			@apply transform -rotate-180;
		}
		
		&:hover {
			.cta-btn-arrow-icon {
				@apply -translate-x-0.5;
			}
			
		}
	}
}

.currency-last-updated,
.localtime-updated {
	html[dir="rtl"] & {
		direction: ltr;
	}
}

.BodyDescriptionTextAlignLeft {
	:deep() {
		
		h1, h2, h3, h4, h5, h6, p, li,
		blockquote, dl, dd, hr, figure,
		pre, div[style*="text-align"] {
			text-align: left !important;
		}
	}
}

.BodyDescriptionTextAlignRight {
	:deep() {
		
		h1, h2, h3, h4, h5, h6, p, li,
		blockquote, dl, dd, hr, figure,
		pre, div[style*="text-align"] {
			text-align: right !important;
		}
	}
}

.DBodyDescriptionTextAlignCenter {
	:deep() {
		
		h1, h2, h3, h4, h5, h6, p, li,
		blockquote, dl, dd, hr, figure,
		pre, div[style*="text-align"] {
			text-align: center !important;
		}
	}
}

</style>
